import Modal from 'components/common/modal';
import { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { Button } from 'antd';
import useStores from 'hooks/use-stores.hooks';
import { useLocation, useNavigate } from 'react-router';
import styled from 'types/theme.types';
import { useHasPermission } from 'hooks/use-has-permission';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { links } from 'constants/external-links';
import { isMonthlyRunsPayGateError, getMonthlyRunsResetDate } from 'utils/pay-gate.utils';
import { typeStyle } from 'constants/typography';

const PayGate = () => {
  const { payGateStore } = useStores();
  const organizationId = useCurrentOrganizationId();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthorized: canManageBilling } = useHasPermission('MANAGE_BILLING');
  const route = location.pathname + location.search;

  useEffect(() => {
    payGateStore.checkCachedRoute(route);
  }, [payGateStore, route]);

  const onClose = useCallback(() => {
    payGateStore.clearActivePayGateError();
  }, [payGateStore]);

  const onContactUsClick = useCallback(() => {
    onClose();
    navigate(`/organizations/${organizationId}/billing`);
  }, [navigate, onClose, organizationId]);

  const isRunsCap = isMonthlyRunsPayGateError(payGateStore.activePayGateErrorText);

  return (
    <>
      <ModalWithLineBreaks
        open={!!payGateStore.activePayGateErrorText}
        size="large"
        onCancel={onClose}
        titleId={isRunsCap ? 'paygate.runs.title' : 'pay-gate.title'}
        destroyOnClose
        maskClosable
        centered
        footer={
          isRunsCap ? (
            <PayGateFooter>
              <ContactSalesLink href={links.BILLING.CONTACT_US} target="_blank" rel="noopener noreferrer">
                <FormattedMessage id="paygate.runs.contact-sales" />
              </ContactSalesLink>
              {canManageBilling && (
                <Button type="primary" href={links.BILLING.CONTACT_US} target="_blank" rel="noopener noreferrer">
                  <FormattedMessage id="paygate.explore-tier-upgrade" />
                </Button>
              )}
            </PayGateFooter>
          ) : canManageBilling ? (
            <Button type="primary" onClick={onContactUsClick}>
              <FormattedMessage id="paygate.explore-tier-upgrade" />
            </Button>
          ) : null
        }>
        {isRunsCap ? (
          <div>
            <FormattedMessage id="paygate.runs.message" values={{ resetDate: getMonthlyRunsResetDate() }} />
            {!canManageBilling && (
              <div>
                <FormattedMessage id="paygate.contact-admin" />
              </div>
            )}
          </div>
        ) : (
          <>
            <div>{payGateStore.activePayGateErrorText}</div>
            {!canManageBilling && <FormattedMessage id="paygate.contact-admin" />}
          </>
        )}
      </ModalWithLineBreaks>
    </>
  );
};

const ModalWithLineBreaks = styled(Modal)`
  white-space: pre-line;
`;

const PayGateFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ContactSalesLink = styled.a`
  ${typeStyle.labelMd}
`;

export default observer(PayGate);
